import { CloseOutlined, LikeFilled, QuestionCircleFilled } from "@ant-design/icons";
import { IEquipIntroInfo } from "@Types/kindSumReport";
import { Modal, Popover, Skeleton, Tooltip } from "antd";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useMemo } from "react";
import { fetchSafeEquipIntro } from "@Services/kindsum";
import BrandKindModelSelect from "./brandKindModelSelect";
import ImageMerger from "@Components/Compare/imageMerger";
import Cookies from "js-cookie";
import { EItemsDiffType, EPageType } from "@Types/specification";
import { useAtom } from "jotai";
import { initTracking } from "@Lib/jotai/tracking";
import Refresh from "@Components/common/Refresh";

// 實心圓
export const y = <div className='w-12 h-12 min-w-12 min-h-12 max-w-12 max-h-12 bg-[#1D2029] rounded-full' />;
// 空心圓
export const n = (
  <div className='w-12 h-12 min-w-12 min-h-12 max-w-12 max-h-12 border-1 border-solid border-[#1D2029] rounded-full' />
);
// 無
export const none = <div className='w-12 h-1 bg-[#1D2029]' />;

// 文本
export const TextValue = memo(({ value, y, n, none, itemss, best = "", id = "", valueType = "" }: any) => {
  let text = "";
  let minOrMaxRecord: { value: string | null; modelYearId: number | null } = { value: null, modelYearId: null };
  const isOnlyOne = itemss && !!itemss.length ? itemss.filter((item) => item.value !== "").length <= 1 : false;

  // 转为整数，移除小数部分
  const formatNumber = useCallback((value) => {
    if (!isNaN(value) && typeof value === "string") {
      value = parseFloat(value);
    }
    if (typeof value === "number") {
      return value.toLocaleString();
    }
    return value;
  }, []);

  switch (value) {
    case "●":
      text = y;
      break;
    case "○":
      text = n;
      break;
    case "-":
    case null:
    case undefined:
      text = none;
      break;
    default:
      text = value || none;
  }

  // 比最小值、最大值
  if (best && (best === "minimumNumberInString" || best === "maximumNumberInString") && !!value && !isOnlyOne) {
    const regex = /^(-?\d+(\.\d+)?)/;
    let minOrMaxValue: number | null = null;
    const minOrMaxList: { value: number; modelYearId: number }[] = [];

    itemss.forEach((item) => {
      const match = item.value.match(regex);
      if (match) {
        const num = parseFloat(match);
        minOrMaxList.push({ value: num, modelYearId: item.modelYearId });
        if (
          minOrMaxValue === null ||
          (best === "minimumNumberInString" && num < minOrMaxValue) ||
          (best === "maximumNumberInString" && num > minOrMaxValue)
        ) {
          minOrMaxValue = num;
          minOrMaxRecord = { value: item.value, modelYearId: item.modelYearId };
        }
      }
    });
    // 去重保证唯一性
    const uniqueById = Array.from(new Map(minOrMaxList.map((item) => [item.modelYearId, item])).values());
    const maxItemCount = uniqueById.filter((item) => item.value === minOrMaxValue).length;
    if (maxItemCount !== 1) {
      minOrMaxRecord = { value: null, modelYearId: null };
    }
  }

  return (
    <div
      className={`py-12 px-14 ${!!minOrMaxRecord.modelYearId && minOrMaxRecord.modelYearId === id ? "text-[#3264FF]" : "text-[#1D2029]"
        }`}
    >
      {!!minOrMaxRecord.modelYearId && minOrMaxRecord.modelYearId === id && (
        <LikeFilled className='text-[#F87F15] mr-4' />
      )}
      {!!valueType && valueType === "costText" ? formatNumber(text) : text}
    </div>
  );
});

// 保留小数文本
export const TextValueWithTip = memo(({ value }: any) => {
  const values = useMemo(() => JSON.parse(value), [value]);

  return (
    <div className='py-12 px-14 w-full flex items-center justify-start'>
      <span>{values?.value}</span>
      {!!values?.tip && (
        <Tooltip
          placement='right'
          title={<span className='text-[#1D2029] text-14 leading-20 whitespace-pre-line'>{values?.tip}</span>}
          color='#fff'
          overlayInnerStyle={{
            padding: "14px",
            maxWidth: "300px",
          }}
        >
          <QuestionCircleFilled className='text-[#9DA2AF] ml-4' />
        </Tooltip>
      )}
    </div>
  );
});

// 多行文本
export const RepeatedValue = memo(({ value, y, n }: any) => {
  const values = useMemo(() => JSON.parse(value), [value]);

  return (
    <div className='flex flex-col items-start gap-8 py-12 px-14'>
      {values.map((v, vIdx) => (
        <span key={vIdx} className='flex items-center justify-start gap-8'>
          {!!v.value &&
            (v.value === "y" ? (
              y
            ) : v.value === "n" ? (
              n
            ) : (
              <span style={{ flexShrink: "0" }} className='w-auto'>
                {v.value}
              </span>
            ))}
          {!!v.expand && <span className='text-14 max-w-[156px]'>{v.expand}</span>}
        </span>
      ))}
    </div>
  );
});

// 选配套件
export const OptionalKit = memo(({ value }: any) => {
  const kits = useMemo(() => JSON.parse(value), [value]);
  return (
    <div className='flex flex-col items-start gap-14 py-12 px-14'>
      {kits.map((kit, idx) => (
        <span key={idx}>
          {kit.title}
          {!!kit.price && `(${kit.price})`}
          {!!kit.description && (
            <Tooltip
              title={<div className='text-14 text-[#1D2029] py-8 px-6 max-w-[484px]'>{kit.description}</div>}
              color='#fff'
              overlayStyle={{ maxWidth: "484px" }}
            >
              <QuestionCircleFilled style={{ color: "#9DA2AF", fontSize: "14px", marginLeft: "4px" }} />
            </Tooltip>
          )}
        </span>
      ))}
    </div>
  );
});

// 颜色
export const ColorValue = memo(({ value }: any) => {
  const colors = useMemo(() => JSON.parse(value), [value]);
  return (
    <div className='flex flex-col items-start gap-10 w-full py-12 px-14'>
      {colors.map((colorObj, idx) => (
        <span key={idx} className='flex items-start justify-between w-full'>
          <div className='flex items-start justify-start gap-8'>
            {colorObj.color.split("+").length > 1 ? (
              <div className='w-14 h-14 rounded-[2px] border-1 border-solid border-[#656873] flex flex-col mt-2'>
                <span className='w-full h-[7px]' style={{ backgroundColor: colorObj.color.split("+")[0] }} />
                <span className='w-full h-[7px]' style={{ backgroundColor: colorObj.color.split("+")[1] }} />
              </div>
            ) : (
              <div
                className='w-14 h-14 rounded-[2px] border-1 border-solid border-[#656873] mt-2'
                style={{ backgroundColor: colorObj.color.split("+")[0], borderRadius: "2px" }}
              />
            )}
            <span className='w-108'>{colorObj.name}</span>
          </div>
          <p className='text-[#656873] text-14 leading-20'>{colorObj.price}</p>
        </span>
      ))}
    </div>
  );
});

// 图例
export const LegendItem = memo(({ icon, label }: any) => (
  <p className='flex items-center gap-4 text-14 font-[400]'>
    {icon} {label}
  </p>
));

// 安全装备介绍
export const EquipIntroInfoModels = memo(({ equipIntroId, isModalOpenEquip, setIsModalOpenEquip }: any) => {
  const [isModalOpenEquipImg, setIsModalOpenEquipImg] = useState(false);
  const [loadingEquip, setLoadingEquip] = useState(true);
  const [equipIntroInfo, setEquipIntroInfo] = useState<IEquipIntroInfo>();

  // 获取安全装备介绍
  const handleIntroClick = async (introId) => {
    setLoadingEquip(true);

    try {
      const res: { status; data } = await fetchSafeEquipIntro({
        id: introId,
      });
      if (res.status === 12000) {
        setEquipIntroInfo(res.data);
      }
    } catch (error) { }
  };

  useEffect(() => {
    equipIntroId !== -1 && handleIntroClick(equipIntroId);
  }, [equipIntroId]);

  return (
    <Modal
      open={isModalOpenEquip}
      footer={null}
      maskClosable={true}
      destroyOnClose={false}
      centered={true}
      width={720}
      bodyStyle={{ padding: "0", minWidth: "720px" }}
      onCancel={() => setIsModalOpenEquip(false)}
    >
      <div className='w-full h-48 flex items-center rounded-tl-8 rounded-tr-8 bg-[#F1F8FF]'>
        <img
          className='w-100 h-22 ml-20'
          src='https://photo.8891.com.tw/2024/09/10/1725947855468790.png'
          alt='8891新車'
        />
      </div>
      <div className='pt-20 pr-10 pl-20 pb-30 flex rounded-bl-8 rounded-br-8'>
        {!!equipIntroInfo?.image && (
          <div className='relative mr-20' onClick={() => setIsModalOpenEquipImg(!isModalOpenEquipImg)}>
            {loadingEquip && <Skeleton.Image active={false} style={{ width: "300px", height: "200px" }} />}
            <img
              className={`${loadingEquip ? "hidden" : ""} w-[300px] h-[200px]`}
              src={equipIntroInfo?.image}
              alt=''
              id='EquipIntroInfoImage'
              onLoad={() => setLoadingEquip(false)}
            />
          </div>
        )}

        <div className='flex flex-col text-38 pr-10'>
          <div className='flex items-end'>
            <h1 className='text-[#1D2029] m-0 font-semibold leading-[36px] text-24'>
              {equipIntroInfo?.title || "-"}
              {!!equipIntroInfo?.title && !!equipIntroInfo?.unit ? `(${equipIntroInfo?.unit})` : ""}
            </h1>
          </div>

          <div className='mt-14 text-14 flex items-start justify-start'>
            <p className={`text-[#656873] leading-20 ${!!equipIntroInfo?.image ? "w-[360px]" : "w-full"}`}>
              {equipIntroInfo?.workingPrinciple || "-"}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
});

// 推荐车型
export const RecommendKindsList = memo(
  ({
    pageType,
    filterAdsIds,
    disabledModels,
    addRecommendKindsClick,
    recommendKindsList,
    modelStyle = {},
  }: {
    filterAdsIds: any;
    addRecommendKindsClick: (id: string) => void;
    disabledModels: any[];
    recommendKindsList: any[];
    modelStyle?: React.CSSProperties;
    pageType?: EPageType;
  }) => {
    const [tracking] = useAtom(initTracking);

    const isSpecPage = pageType === EPageType.SPEC;
    const isSummaryPage = pageType === EPageType.SUMMARY;

    // 添加车型列表 - 埋点
    const handleAddRecommendKindsExposured = (item) => {
      if (!tracking) return;
      isSpecPage &&
        tracking.trackEvent({
          name: "element_exposured",
          params: {
            page_sub_business: "新車資料庫",
            page_business: "新車",
            trigger_id: "4097",
            page_id: "622",
            trigger_name: "+比較",
            object_id: item.myid || "",
            next_spm: "3.622.810.2172",
            page: "PK比較結果頁",
            channel: "新車",
            element_id: "+比較",
            element: "按鈕",
            module: "車型列表",
            tracker_method: "config",
            object_type: "myid",
            recommend_by: window.MODEL_YEAR_IDS,
          },
        });
      isSummaryPage &&
        tracking.trackEvent({
          name: "element_exposured",
          params: {
            page_business: "新車",
            trigger_id: "4136",
            page_id: "721",
            trigger_name: "推薦車型+比較",
            next_spm: "3.721.864.2320",
            page: "PK比較綜合比較頁",
            channel: "新車",
            element_id: "推薦車型+比較",
            element: "按鈕",
            module: "推薦車型",
            tracker_method: "config",
            object_id: item.myid || "",
            object_type: "myid",
            recommend_by: window.MODEL_YEAR_IDS,
          },
        });
    };

    const handleAddRecommendKindsClick = (item) => {
      addRecommendKindsClick(item);
      if (!tracking) return;
      isSpecPage &&
        tracking.trackEvent({
          name: "element_clicked",
          params: {
            page_sub_business: "新車資料庫",
            page_business: "新車",
            trigger_id: "4098",
            page_id: "622",
            trigger_name: "+比較",
            object_type: "myid",
            next_spm: "3.622.810.2172",
            page: "PK比較結果頁",
            channel: "新車",
            element_id: "+比較",
            element: "按鈕",
            module: "車型列表",
            tracker_method: "config",
            object_id: item.myid || "",
            recommend_by: window.MODEL_YEAR_IDS,
          },
        });

      isSummaryPage &&
        tracking.trackEvent({
          name: "element_clicked",
          params: {
            page_business: "新車",
            trigger_id: "4137",
            page_id: "721",
            trigger_name: "推薦車型+比較",
            object_type: "myid",
            next_spm: "3.721.864.2320",
            page: "PK比較綜合比較頁",
            channel: "新車",
            element_id: "推薦車型+比較",
            element: "按鈕",
            module: "推薦車型",
            tracker_method: "config",
            object_id: item.myid || "",
            recommend_by: window.MODEL_YEAR_IDS,
          },
        });
    };

    // 下拉添加车型 - 埋点
    const handleBrandKindSelectSpecExposured = () => {
      if (!tracking) return;
      isSpecPage &&
        tracking.trackEvent({
          name: "element_exposured",
          params: {
            page_sub_business: "新車資料庫",
            page_business: "新車",
            trigger_id: "4099",
            page_id: "622",
            trigger_name: "添加車型",
            next_spm: "3.622.810.2173",
            page: "PK比較結果頁",
            channel: "新車",
            element_id: "添加車型",
            element: "按鈕",
            module: "車型列表",
            tracker_method: "config",
          },
        });

      isSummaryPage &&
        tracking.trackEvent({
          name: "element_exposured",
          params: {
            page_business: "新車",
            trigger_id: "4129",
            page_id: "721",
            trigger_name: "添加車型",
            next_spm: "3.721.859.2325",
            page: "PK比較綜合比較頁",
            channel: "新車",
            element_id: "添加車型",
            element: "按鈕",
            module: "車型列表",
            tracker_method: "config",
          },
        });
    };

    const handleBrandKindSelectSpecClick = (data) => {
      if (!tracking) return;
      isSpecPage &&
        tracking.trackEvent({
          name: "element_clicked",
          params: {
            page_sub_business: "新車資料庫",
            page_business: "新車",
            trigger_id: "4100",
            page_id: "622",
            trigger_name: "添加車型",
            object_type: "myid",
            next_spm: "3.622.810.2173",
            page: "PK比較結果頁",
            channel: "新車",
            element_id: "添加車型",
            element: "按鈕",
            module: "車型列表",
            tracker_method: "config",
            object_id: data.myid || "",
          },
        });

      isSummaryPage &&
        tracking.trackEvent({
          name: "element_clicked",
          params: {
            page_business: "新車",
            trigger_id: "4130",
            page_id: "721",
            trigger_name: "添加車型",
            object_type: "myid",
            next_spm: "3.721.859.2325",
            page: "PK比較綜合比較頁",
            channel: "新車",
            element_id: "添加車型",
            element: "按鈕",
            module: "車型列表",
            tracker_method: "config",
            object_id: data.myid || "",
          },
        });
    };

    return (
      <div
        className={`w-270 border-0 border-t-1 border-r-1 border-b-1 border-solid border-[#EDEFF5] sticky`}
        style={{ height: isSpecPage ? "214px" : "264px" }}
      >
        {filterAdsIds.size < 10 ? (
          <>
            {Array.isArray(recommendKindsList) && !!recommendKindsList.length && recommendKindsList.length > 1 && (
              <>
                <img src='https://photo.8891.com.tw/2024/12/16/1734342199870330.png' alt='' className='w-64 h-24' />

                <div className={`flex px-8 gap-8 cursor-pointer ${isSpecPage ? "mb-20 mt-0" : "mb-34 mt-20"}`}>
                  <div
                    data-id={recommendKindsList[0]?.myid}
                    className='flex flex-col items-center text-14 text-[#1D2029] leading-20 gap-10 flex-1'
                    onClick={() => handleAddRecommendKindsClick(recommendKindsList[0])}
                  >
                    <Refresh
                      refresh={() => {
                        handleAddRecommendKindsExposured(recommendKindsList[0]);
                      }}
                    />
                    <Popover
                      content={
                        <div className='max-w-[286px] p-8 text-[#1D2029] text-14 leading-20'>
                          {recommendKindsList[0]?.title}
                        </div>
                      }
                    >
                      <p className='leading-20 line-clamp-4 pt-8 h-88 text-center'>{recommendKindsList[0]?.title}</p>
                    </Popover>
                    +比較
                  </div>
                  <i className='w-[1px] bg-[#EDEFF5] rounded-4' />
                  <div
                    data-id={recommendKindsList[1]?.myid}
                    className='flex flex-col items-center text-14 text-[#1D2029] leading-20 gap-10 flex-1'
                    onClick={() => handleAddRecommendKindsClick(recommendKindsList[1])}
                  >
                    <Refresh
                      refresh={() => {
                        handleAddRecommendKindsExposured(recommendKindsList[1]);
                      }}
                    />
                    <Popover
                      content={
                        <div className='max-w-[286px] p-8 text-[#1D2029] text-14 leading-20'>
                          {recommendKindsList[1]?.title}
                        </div>
                      }
                    >
                      <p className='leading-20 line-clamp-4 pt-8 h-88 text-center'>{recommendKindsList[1]?.title}</p>
                    </Popover>
                    +比較
                  </div>
                </div>
              </>
            )}

            <div
              id='tracking-PkSpecAddKindModule'
              className={`${!recommendKindsList.length ? "flex items-center justify-center w-full h-full" : ""} `}
            >
              <Refresh
                refresh={() => {
                  handleBrandKindSelectSpecExposured();
                }}
              />
              <BrandKindModelSelect
                updataGaGloball={false}
                addKindsSetLocal={false}
                modelStyle={modelStyle}
                disabledModels={disabledModels}
                customSelect={
                  <div className='flex items-center justify-center border-1 border-solid border-[#D9D9D9] rounded-4 w-162 h-32 text-[#1D2029] text-14 mx-auto'>
                    <img
                      src='https://photo.8891.com.tw/2024/12/16/1734343077694566.png'
                      alt=''
                      className='w-10 h-10 mr-4'
                    />
                    添加車型
                  </div>
                }
                onSelectModel={(data) => {
                  handleBrandKindSelectSpecClick(data);
                  addRecommendKindsClick(data);
                }}
              />
            </div>
          </>
        ) : (
          <div className='flex items-center justify-center text-[#656873] text-14 leading-20 w-full h-full text-center'>
            車輛已加滿 <br />
            最多添加10個車型
          </div>
        )}
      </div>
    );
  },
);

// 设置url参数
export const setUrlParams = (modelYearIds, type = window.PAGE_MODEL) => {
  const link =
    type === EPageType.SUMMARY
      ? `${location.origin}/pk/comprehensive?myids=${modelYearIds || ""}`
      : `${location.origin}/photoModel-compare.html?myids=${modelYearIds || ""}`;
  history.replaceState(null, "", link);
};

// 安全氣囊配備 - 蜘蛛图
// '前座正面氣囊' => 'frontSeatAirBag',
// '前座中央氣囊' => 'frontCenterAirBag',
// '膝部氣囊-主駕駛' => 'leftKneeAirBag',
// '膝部氣囊-副駕駛' => 'rightKneeAirBag',
// '前座側面氣囊' => 'frontSideAirBag',
// '側面氣簾' => 'sideCurtainAirBag',
// '後座側面氣囊' => 'rearSideAirBag',
export const EquipImage = memo(({ value, id, itemss }: any) => {
  const values = useMemo(() => JSON.parse(value), [value]);

  // 计算气囊个数的函数
  const { airbagStatus, airbagCount } = useMemo(() => {
    let count = 0;
    const status = {
      frontSeatAirBag: false,
      frontCenterAirBag: false,
      leftKneeAirBag: false,
      rightKneeAirBag: false,
      frontSideAirBag: false,
      sideCurtainAirBag: false,
      rearSideAirBag: false,
    };

    // 遍历所有气囊数据
    values.forEach((item) => {
      if (item.value === "y") {
        // 如果为 'y'，加 2
        if (
          item.key === "frontSeatAirBag" ||
          item.key === "sideCurtainAirBag" ||
          item.key === "rearSideAirBag" ||
          item.key === "frontSideAirBag"
        ) {
          count += 2;
        } else {
          count += 1;
        }

        // 更新气囊状态
        if (status.hasOwnProperty(item.key)) {
          status[item.key] = true;
        }
      }
    });

    return { airbagStatus: status, airbagCount: count };
  }, [values]);

  // 计算最多的那个数
  const { allAirbagCount } = useMemo(() => {
    const allAirbagList = itemss.map((item) => {
      const parsedValue = JSON.parse(item.value);
      const relevantKeys = new Set(["frontSeatAirBag", "sideCurtainAirBag", "rearSideAirBag", "frontSideAirBag"]);

      const finalValue = parsedValue.reduce((count, { key, value }) => {
        if (value === "y") {
          return count + (relevantKeys.has(key) ? 2 : 1);
        }
        return count;
      }, 0);

      return {
        value: finalValue,
        id: item.modelYearId,
      };
    });

    // 找到最大值和最大值的数量
    const maxItem = allAirbagList.reduce((max, current) => (current.value > max.value ? current : max));

    const maxItemCount = allAirbagList.filter((item) => item.value === maxItem.value).length;

    // 如果最大值有多个或没有最大值，则返回 null
    if (maxItemCount !== 1) {
      return { allAirbagCount: null };
    }

    return { allAirbagCount: maxItem };
  }, [itemss]);

  const images = [
    "https://photo.8891.com.tw/2025/01/02/1735800075691907.png", // 车底图
    airbagStatus.frontSeatAirBag
      ? "https://photo.8891.com.tw/2025/01/02/1735801973444164.png"
      : "https://photo.8891.com.tw/2025/01/02/1735801981040682.png", // 前座正面氣囊
    airbagStatus.frontCenterAirBag
      ? "https://photo.8891.com.tw/2025/01/02/1735801973218851.png"
      : "https://photo.8891.com.tw/2025/01/02/1735801980772301.png", // 前座中央氣囊
    airbagStatus.leftKneeAirBag
      ? "https://photo.8891.com.tw/2025/01/02/1735801973708649.png"
      : "https://photo.8891.com.tw/2025/01/02/1735801980699501.png", // 主駕膝部氣囊
    airbagStatus.rightKneeAirBag
      ? "https://photo.8891.com.tw/2025/01/02/1735801973721627.png"
      : "https://photo.8891.com.tw/2025/01/02/1735801980733348.png", // 副駕膝部氣囊
    airbagStatus.frontSideAirBag
      ? "https://photo.8891.com.tw/2025/01/02/1735801973491380.png"
      : "https://photo.8891.com.tw/2025/01/02/1735801980620947.png", // 前座側面氣囊
    airbagStatus.sideCurtainAirBag
      ? "https://photo.8891.com.tw/2025/01/02/1735801973409449.png"
      : "https://photo.8891.com.tw/2025/01/02/1735801980808540.png", // 側面氣簾
    airbagStatus.rearSideAirBag
      ? "https://photo.8891.com.tw/2025/01/02/1735801973421060.png"
      : "https://photo.8891.com.tw/2025/01/02/1735801980896275.png", // 後座側面氣囊
  ];
  return (
    <div className='flex flex-col justify-center items-center pt-2 mx-auto h-[260px]'>
      <div className='flex items-center justify-center gap-4 mb-14 text-14'>
        {allAirbagCount?.id === id && <LikeFilled className='text-[#F87F15]' />}
        <p className={`${allAirbagCount?.id === id ? "text-[#3264FF]" : "text-[#1D2029]"}`}>
          共<span className='text-[#3264FF]'>{airbagCount ?? 0}</span>個氣囊
        </p>
      </div>
      <ImageMerger images={images} />
    </div>
  );
});

// 安全配備 - 列表
export const EquipList = memo(({ value, id, itemss }: any) => {
  const values = useMemo(() => JSON.parse(value), [value]);

  // 计算所有个数
  const { count } = useMemo(() => {
    let count = 0;
    // 遍历所有数据
    values.forEach((item) => {
      if (item.value === "y") {
        count += 1;
      }
    });
    return { count };
  }, [values]);

  // 计算最大的个数
  const { maxItem } = useMemo(() => {
    const allList = itemss.map((item) => ({
      value: JSON.parse(item.value).filter(({ value }) => value === "y").length,
      id: item.modelYearId,
    }));
    const maxItem = allList.reduce((max, current) => (current.value > max.value ? current : max));

    // 如果最大值有多个或没有最大值，则返回 null
    const maxItemCount = allList.filter((item) => item.value === maxItem.value).length;
    if (maxItemCount !== 1) {
      return { maxItem: null };
    }

    return { maxItem };
  }, [itemss]);

  return (
    <div className='flex flex-col justify-center items-center mx-auto py-14 h-full'>
      <div className='flex items-center justify-center gap-4 mb-14 text-14'>
        {maxItem?.id === id && <LikeFilled className='text-[#F87F15]' />}
        <p className={`${maxItem?.id === id ? "text-[#3264FF]" : "text-[#1D2029]"}`}>
          共<span className='text-[#3264FF]'>{count}</span>項
        </p>
      </div>
      <div className='grid grid-cols-2 gap-10 justify-center items-center'>
        {!!values.length &&
          values.map((item, index) => (
            <p
              key={index}
              className={`w-120 bg-[#F6F7FB] text-12 flex items-center py-[5px] px-6 h-full justify-center text-center rounded-4 ${item.value === "y" ? "text-[#1D2029]" : "text-[#939BAF] line-through"
                }`}
            >
              {item.title}
            </p>
          ))}
      </div>
    </div>
  );
});

// 長寬高/軸距
export const LengthWidthHeight = memo(({ value, id, itemss }: any) => {
  const values = useMemo(() => JSON.parse(value), [value]);

  const { length, width, height, wheelbase } = useMemo(() => {
    const result = values.reduce((acc, item) => {
      if (item.key === "length") acc.length = item.value;
      if (item.key === "width") acc.width = item.value;
      if (item.key === "height") acc.height = item.value;
      if (item.key === "wheelbase") acc.wheelbase = item.value;
      return acc;
    }, {});

    return result;
  }, [values]);

  // 计算最大的个数
  const { maxItem } = useMemo(() => {
    const maxData = {
      maxLength: { value: 0, id: null, count: 0 },
      maxWidth: { value: 0, id: null, count: 0 },
      maxHeight: { value: 0, id: null, count: 0 },
      maxWheelbase: { value: 0, id: null, count: 0 },
    };

    const updateMaxValue = (key, numericValue, modelYearId) => {
      const dataKey = `max${key.charAt(0).toUpperCase() + key.slice(1)}`;
      if (numericValue > maxData[dataKey].value) {
        maxData[dataKey] = { value: numericValue, id: modelYearId, count: 1 };
      } else if (numericValue === maxData[dataKey].value) {
        maxData[dataKey].count += 1;
      }
    };

    itemss.forEach((item) => {
      const parsedValues = JSON.parse(item.value);
      parsedValues.forEach(({ key, value }) => {
        const numericValue = parseFloat(value);
        updateMaxValue(key, numericValue, item.modelYearId);
      });
    });

    // 清除有重复最大值的项
    Object.keys(maxData).forEach((key) => {
      if (maxData[key].count > 1) {
        maxData[key] = { value: 0, id: null, count: 0 };
      }
    });

    return { maxItem: maxData };
  }, [itemss]);

  return (
    <div className='flex flex-col justify-center items-center w-full py-14 relative h-200'>
      <div className="bg-[url('https://photo.8891.com.tw/2025/01/03/1735872366481323.png')] bg-cover bg-center w-230 h-128 text-14">
        <p
          className={`absolute top-[92px] right-[40px] ${maxItem?.maxWheelbase.id === id ? "text-[#3264FF]" : "text-[#1D2029]"
            }`}
        >
          軸距{wheelbase ? wheelbase + "mm" : "-"}
        </p>
        <p
          className={`absolute top-[14px] left-0 right-0 text-center ${maxItem?.maxHeight.id === id ? "text-[#3264FF]" : "text-[#1D2029]"
            }`}
        >
          高{height ? height + "mm" : "-"}
        </p>
        <p
          style={{ transform: "rotate(5deg)" }}
          className={`absolute bottom-[16px] left-[30px] ${maxItem?.maxWidth.id === id ? "text-[#3264FF]" : "text-[#1D2029]"
            }`}
        >
          寬{width ? width + "mm" : "-"}
        </p>
        <p
          style={{ transform: "rotate(-15deg)" }}
          className={`absolute bottom-[24px] right-[30px] ${maxItem?.maxLength.id === id ? "text-[#3264FF]" : "text-[#1D2029]"
            }`}
        >
          長{length ? length + "mm" : "-"}
        </p>
      </div>
    </div>
  );
});

// 座椅功能
export const SeatFunction = memo(({ value }: any) => {
  const values = useMemo(() => JSON.parse(value), [value]);

  if (values.length === 0) {
    return <span className='py-12 px-14'>{none}</span>;
  }

  return (
    <div className='flex flex-col justify-center items-center w-full p-14 relative'>
      <div className='flex flex-wrap justify-start items-center gap-10 w-full'>
        {!!values.length &&
          values.map((item, index) => (
            <p key={index} className='px-14 py-4 rounded-4 bg-[#F6F7FB] text-[#1D2029] text-12'>
              {item.title}
            </p>
          ))}
      </div>
    </div>
  );
});

// 删除广告车型后记录cookie，7天内不在显示
export const addMyidToCookie = (adMyid) => {
  const exMyidsValue = Cookies.get("exMyids");
  const resultMyid = !exMyidsValue ? adMyid : exMyidsValue + "," + adMyid;
  Cookies.set("exMyids", resultMyid, { expires: 7 });
};

// 处理埋点数据上报
export const getDescriptions = (types: string[]): string[] => {
  return types.map((type) => {
    switch (type) {
      case EItemsDiffType.HIGHLIGHT_DIFF:
        return "高亮不同項";
      case EItemsDiffType.HIDE_IDENTICAL:
        return "隱藏相同項";
      case EItemsDiffType.HIDE_NONE:
        return "隱藏暫無項";
      default:
        return "";
    }
  });
};

// 超长文本
export const LongText = memo(({ model, pageType = false }: any) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const checkOverflow = () => {
    if (textRef.current) {
      const { scrollHeight, clientHeight } = textRef.current;
      const isTextOverflowing = scrollHeight > clientHeight;
      setIsOverflow(isTextOverflowing);
    }
  };

  useEffect(() => {
    setTimeout(checkOverflow, 0);
  }, [model.name]);

  const lineClamp = pageType ? "line-clamp-3 h-60" : "line-clamp-2 h-40";
  const mt = pageType ? "mt-6" : "mt-4";

  const pText = (
    <p ref={textRef} className={`text-14 text-[#1D2029] font-[600] leading-20 ${lineClamp}`}>
      {model.name}
    </p>
  );

  return isOverflow ? (
    <Popover
      content={<div className='max-w-[286px] p-8 text-[#1D2029] text-14 leading-20'>{model.name}</div>}
      overlayStyle={{ position: "fixed", top: 0 }}
      className={`w-full px-14 ${mt} mb-8`}
    >
      {pText}
    </Popover>
  ) : (
    <div className={`w-full px-14 ${mt} mb-8`}>{pText}</div>
  );
});


// PK比較調研
export const PKQuestion = memo(({ onClose, trackingId }: { onClose: () => void, trackingId: string }) => {
  return <div className='w-158 h-66 fixed right-10 bottom-10 z-99' id={trackingId}>
    <img
      src='https://photo.8891.com.tw/2025/03/18/1742282454960333.png'
      alt=''
      className='w-full h-full'
    />
    <CloseOutlined className='absolute top-16 right-10 z-2' style={{ color: '#fff' }} onClick={onClose} />
    <a href='https://forms.gle/FW255Tg5TqZRfshw9' target="_blank" className="absolute bottom-[17px] left-60 text-white hover:text-white flex flex-col items-start justify-center leading-16 text-[11px]">
      <span>PK比較調研</span>
      <span>您的意見很重要~</span>
    </a>
  </div>;
});
